/**
 * Utility module to handle scoped caching of JSON data
 */

/**
 * Item loading function.
 * @callback LoaderCallback
 * @param {string} key
 * @param {?string} scope
 */

class Cache {
	/**
	 * @param {Storage} storage
	 * @param {?string} scope
	 */
	constructor(storage, scope) {
		this.storage = storage;
		this.scope = scope;
	}

	get(key, defaultValue=null) {
		if (this.scope) key = this.scope + "." + key;
		let raw = this.storage.getItem(key);
		if (raw == null) return defaultValue;
		return Cache.unpack(raw).data;
	}

	set(key, value) {
		if (this.scope) key = this.scope + "." + key;
		this.storage.setItem(key, Cache.pack(value));
		return this;
	}

	remove(key) {
		if (this.scope) key = this.scope + "." + key;
		this.storage.removeItem(key);
	}

	clear() {
		if (this.scope) {
			// clear only the scope
			const keyStart = this.scope + ".";
			const toRemove = [];
			for (let i = 0; i < this.storage.length; i++) {
				const key = this.storage.key(i);
				if (key.startsWith(keyStart)) toRemove.push(key);
			}
			for (let key of toRemove) this.storage.removeItem(key);
		} else {
			this.storage.clear();	// clear all
		}
	}

	/**
	 *
	 * @param {string} key
	 * @param {LoaderCallback} loader
	 * @param {number} maxAgeMs Maximum age milliseconds. If the item is too old it will be reloaded using given loader
	 * @return {Promise<*>}
	 */
	async getOrLoad(key, loader, maxAgeMs=0) {
		const fullKey = this.scope ? this.scope + "." + key : key;
		let raw = this.storage.getItem(fullKey);
		if (raw == null) {
			const data = await loader(key, this.scope);
			if (data !== undefined)	this.storage.setItem(fullKey, Cache.pack(data));
			return data;
		} else {
			const unpacked = Cache.unpack(raw);
			if (maxAgeMs > 0) {
				// let's check age
				if (((new Date().getTime()) - unpacked.created.getTime()) > maxAgeMs) {
					// Must reload
					const fresh = await loader(key, this.scope);
					this.storage.setItem(fullKey, Cache.pack(fresh));
					return fresh;
				}
			}
			return unpacked.data;
		}
	}

	static pack(data) {
		return "::" + (new Date().getTime()) + ":" + (typeof data) + ":" + JSON.stringify(data);
	}

	/**
	 * @param {?string} raw
	 * @return {{data: *, created: Date}}
	 */
	static unpack(raw) {
		let retval = { created: new Date(), data: null };
		if (raw == null || typeof raw === "undefined") {
			return retval;
		}
		if (raw.startsWith("::")) {
			const typeIdx = raw.indexOf(":", 2);
			const dataIdx = raw.indexOf(":", typeIdx+1);
			const rawData = raw.substring(dataIdx + 1);
			retval.created.setTime(parseInt(raw.substring(2, typeIdx)));
			retval.data = JSON.parse(rawData);
		} else {
			retval.data = raw;
		}
		return retval;
	}
}

export default {
	local(scope = null) {
		return new Cache(window.localStorage, scope);
	},

	session(scope = null) {
		return new Cache(window.sessionStorage, scope);
	}
};

